<template>
  <div v-if="visible" class="gm-dock-layer">
    <transition name="gm-dock-rise">
      <nav v-if="open" class="gm-dock" aria-label="Game Master tools">
        <div v-for="group in groups" :key="group.key" class="gm-dock__group">
          <span class="gm-dock__group-label">{{ group.label }}</span>
          <div class="gm-dock__tools">
            <router-link
                v-for="tool in group.tools"
                :key="tool.key"
                :to="tool.to"
                class="gm-dock__tool"
                :class="{ 'is-active': activeTool && activeTool.key === tool.key }"
                :aria-current="activeTool && activeTool.key === tool.key ? 'page' : undefined"
            >
              <span class="gm-dock__glyph">
                <GMToolIcon :name="tool.icon"/>
              </span>
              <span class="gm-dock__caption">{{ tool.label }}</span>
              <span class="gm-dock__tip">{{ tool.description }}</span>
            </router-link>
          </div>
        </div>
      </nav>
    </transition>

    <div class="gm-dock__bar">
      <!-- The way out. The GM area replaces the player menu bar entirely, so
           without this the only route back to the lobby is the browser's back
           button — and there is none at all after a reload lands here. -->
      <router-link class="gm-dock__exit" :to="{ name: 'PlayerHomeDashboard' }" title="Back to the player lobby">
        <span class="gm-dock__handle-glyph">
          <GMToolIcon name="exit"/>
        </span>
        <span class="gm-dock__handle-label">Lobby</span>
      </router-link>

      <button
          type="button"
          class="gm-dock__handle"
          :class="{ 'is-open': open }"
          :aria-expanded="open ? 'true' : 'false'"
          :title="open ? 'Hide GM tools (Alt+G)' : 'Show GM tools (Alt+G)'"
          @click="toggle"
      >
        <span class="gm-dock__handle-glyph">
          <GMToolIcon :name="activeTool ? activeTool.icon : 'command-center'"/>
        </span>
        <span class="gm-dock__handle-label">{{ activeTool ? activeTool.label : 'GM Tools' }}</span>
        <svg class="gm-dock__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M6 15l6-6 6 6"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import GMToolIcon from '@/components/GameMaster/GMToolIcon.vue';
import {GM_TOOL_GROUPS, resolveActiveGmTool} from '@/config/gameMasterTools';

const STORAGE_KEY = 'dx_gm_dock_open';

export default {
  name: 'GameMasterToolbar',
  components: {GMToolIcon},
  data() {
    return {
      groups: GM_TOOL_GROUPS,
      open: false
    };
  },
  computed: {
    visible() {
      return this.$route.path.startsWith('/game-master') && this.$store.getters.isGameMaster;
    },
    activeTool() {
      return resolveActiveGmTool(this.$route.path);
    }
  },
  created() {
    try {
      this.open = window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch (err) {
      this.open = false;
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    toggle() {
      this.setOpen(!this.open);
    },
    setOpen(value) {
      this.open = value;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(value));
      } catch (err) {
        /* storage unavailable */
      }
    },
    handleKeydown(event) {
      if (!this.visible) {
        return;
      }
      if (event.altKey && (event.key === 'g' || event.key === 'G')) {
        event.preventDefault();
        this.toggle();
        return;
      }
      if (event.key === 'Escape' && this.open) {
        this.setOpen(false);
      }
    }
  }
};
</script>

<style scoped>
.gm-dock-layer {
  position: fixed;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  z-index: 900;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  max-width: 100vw;
}

.gm-dock-layer > * {
  pointer-events: auto;
}

.gm-dock {
  display: flex;
  align-items: stretch;
  gap: 0.35rem;
  padding: 0.6rem 0.9rem;
  margin-bottom: 0.35rem;
  max-width: calc(100vw - 1.5rem);
  overflow-x: auto;
  scrollbar-width: none;
  background: linear-gradient(180deg, var(--gm-surface-1), var(--gm-surface-0));
  border: 1px solid var(--gm-border);
  border-radius: var(--gm-radius-lg);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.6), var(--gm-shadow);
  backdrop-filter: blur(6px);
}

.gm-dock::-webkit-scrollbar {
  height: 0;
  width: 0;
}

.gm-dock__group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0 0.5rem;
  border-right: 1px solid var(--gm-border-dim);
}

.gm-dock__group:last-child {
  border-right: none;
}

.gm-dock__group-label {
  font-family: var(--gm-font-display);
  font-size: 0.6rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--gm-text-dim);
  text-align: center;
}

.gm-dock__tools {
  display: flex;
  gap: 0.25rem;
}

.gm-dock__tool {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  width: 5.6rem;
  padding: 0.4rem 0.25rem 0.35rem;
  border: 1px solid transparent;
  border-radius: var(--gm-radius);
  color: var(--gm-text-muted);
  font-style: normal;
  text-decoration: none;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
}

.gm-dock__tool:hover {
  color: var(--gm-accent);
  background: var(--gm-accent-soft);
  border-color: var(--gm-border);
  text-decoration: none;
}

.gm-dock__tool:active {
  transform: scale(0.96);
}

.gm-dock__tool.is-active {
  color: var(--gm-accent-hi);
  background: rgba(255, 215, 0, 0.12);
  border-color: var(--gm-border-hi);
  box-shadow: inset 0 0 12px rgba(255, 215, 0, 0.12);
}

.gm-dock__glyph {
  width: 1.6rem;
  height: 1.6rem;
}

.gm-dock__caption {
  font-family: var(--gm-font-display);
  font-size: 0.52rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  line-height: 1.15;
  text-align: center;
  width: 100%;
  overflow-wrap: anywhere;
}

.gm-dock__tip {
  position: absolute;
  bottom: calc(100% + 0.45rem);
  left: 50%;
  transform: translateX(-50%);
  padding: 0.3rem 0.55rem;
  background: rgba(0, 0, 0, 0.94);
  border: 1px solid var(--gm-border);
  border-radius: var(--gm-radius-sm);
  color: var(--gm-accent);
  font-family: var(--gm-font-body);
  font-size: 0.7rem;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.gm-dock__tool:hover .gm-dock__tip {
  opacity: 1;
}

.gm-dock__bar {
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
}

.gm-dock__exit {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.85rem;
  background: linear-gradient(180deg, var(--gm-surface-1), var(--gm-surface-0));
  border: 1px solid var(--gm-border);
  border-bottom: none;
  border-radius: 0.6rem 0.6rem 0 0;
  color: var(--gm-text-muted);
  text-decoration: none;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.gm-dock__exit:hover {
  color: var(--gm-accent);
  border-color: var(--gm-border-hi);
  text-decoration: none;
}

.gm-dock__handle {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.85rem;
  background: linear-gradient(180deg, var(--gm-surface-1), var(--gm-surface-0));
  border: 1px solid var(--gm-border);
  border-bottom: none;
  border-radius: 0.6rem 0.6rem 0 0;
  color: var(--gm-text-muted);
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.gm-dock__handle:hover {
  color: var(--gm-accent);
  border-color: var(--gm-border-hi);
}

.gm-dock__handle.is-open {
  color: var(--gm-accent-hi);
}

.gm-dock__handle-glyph {
  width: 1.05rem;
  height: 1.05rem;
}

.gm-dock__handle-label {
  font-family: var(--gm-font-display);
  font-size: 0.64rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.gm-dock__chevron {
  width: 0.85rem;
  height: 0.85rem;
  transition: transform 0.2s ease;
}

.gm-dock__handle.is-open .gm-dock__chevron {
  transform: rotate(180deg);
}

.gm-dock-rise-enter-active,
.gm-dock-rise-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.gm-dock-rise-enter-from,
.gm-dock-rise-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 900px) {
  .gm-dock {
    padding: 0.45rem 0.5rem;
  }

  .gm-dock__group {
    padding: 0 0.3rem;
  }

  .gm-dock__tool {
    width: 2.9rem;
  }

  .gm-dock__caption {
    display: none;
  }

  .gm-dock__group-label {
    font-size: 0.52rem;
  }
}
</style>
